import React from "react";
import { Cog, Bot, Radar, Cpu, Boxes, Combine, Check } from "lucide-react";
import { PageHero, SectionLabel, Reveal, ArrowLink } from "../components/common";
import { CtaBand } from "./Home";
import { IMG, CAPABILITIES, DESIGN_RULES } from "../mock";

const ICONS = { Cog, Bot, Radar, Cpu, Boxes, Combine };

export default function Engineering() {
  return (
    <main>
      <PageHero
        index="02"
        eyebrow="A Garry Industries Ventures · Engineering & Technology"
        title="Disciplined engineering, from concept to component."
        subtitle="Our machines are engineering concepts, not fantasy vehicles. Each subsystem is purposeful, each component contributes to function, and everything is conceived to be built and serviced."
        image={IMG.tamping}
      />

      {/* Capabilities */}
      <section className="bg-[#0a0a0a] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal><SectionLabel>A Garry Industries Ventures · Capabilities</SectionLabel></Reveal>
          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_1fr] lg:gap-20">
            <Reveal><h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight">Multi-disciplinary by design.</h2></Reveal>
            <Reveal delay={100} className="flex items-center"><p className="text-neutral-400 leading-relaxed">Railway machinery sits at the intersection of heavy engineering, robotics and software. We build capability across the full stack.</p></Reveal>
          </div>
          <div className="mt-16 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c, i) => {
              const Icon = ICONS[c.icon];
              return (
                <Reveal key={i} delay={(i % 3) * 70} className="group bg-[#0a0a0a] p-8 hover:bg-[#111] transition-colors">
                  <div className="flex items-center justify-between">
                    <Icon size={26} strokeWidth={1.5} className="text-[#c3e63c]" />
                    <span className="font-mono2 text-xs text-neutral-600">{c.id}</span>
                  </div>
                  <h3 className="font-display mt-6 text-xl font-semibold">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400">{c.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Design rules */}
      <section className="bg-[#f2f1ec] text-[#0a0a0a] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-20 lg:items-start">
            <div>
              <Reveal><SectionLabel light>A Garry Industries Ventures · Machine Design Rules</SectionLabel></Reveal>
              <Reveal><h2 className="font-display mt-6 text-4xl md:text-5xl font-bold leading-[1.03] tracking-tight">Nothing decorative. Everything engineered.</h2></Reveal>
              <Reveal delay={100}><p className="mt-6 text-[#4a4a44] leading-relaxed max-w-lg">Every concept machine adheres to a strict engineering checklist — the same discipline that separates a manufacturable product from a rendering.</p></Reveal>
              <Reveal delay={160} className="mt-10 overflow-hidden border border-[#0a0a0a]/10 group">
                <img src={IMG.gears} alt="Exploded engineering view" className="img-zoom h-72 w-full object-cover" />
              </Reveal>
            </div>
            <div className="grid gap-px bg-[#d8d7d0] border border-[#d8d7d0]">
              {DESIGN_RULES.map((r, i) => (
                <Reveal key={i} delay={i * 40} className="flex items-center gap-4 bg-[#f2f1ec] px-5 py-4">
                  <Check size={16} className="text-[#8ba817] shrink-0" />
                  <span className="text-sm md:text-[15px]">{r}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Standards */}
      <section className="relative overflow-hidden bg-[#0a0a0a] py-24 md:py-32">
        <img src={IMG.excavator} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/90 to-[#0a0a0a]/60" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal><SectionLabel>A Garry Industries Ventures · Standards & Rigour</SectionLabel></Reveal>
          <Reveal><h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight max-w-3xl">Engineered to global standards.</h2></Reveal>
          <div className="mt-8 max-w-2xl space-y-5">
            <Reveal delay={80}><p className="text-neutral-300 leading-relaxed">We design to the demanding realities of railway operation — safety, reliability, maintainability and interoperability. Our engineering targets alignment with the expectations of railway authorities, inspection regimes and international best practice.</p></Reveal>
            <Reveal delay={140}><p className="text-neutral-300 leading-relaxed">Validation is model-based first: simulation, analysis and digital-twin testing de-risk each platform long before physical prototyping.</p></Reveal>
          </div>
          <div className="mt-10"><ArrowLink to="/ai-platform">Digital engineering & AI platform</ArrowLink></div>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
